import {Button, Col, Form, InputGroup, Modal, Row, Spinner} from 'react-bootstrap'
import {IAllUserNode, IResCreateMember, IResEditMember} from '../../../types/users'
import {useEffect, useRef, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {CREATE_USER, EDIT_USER} from '../../../gql/mutations/userMutations'
import {parseId} from '../../../helpers'
import {GET_ROLES_GROUP} from '../../../gql/queries/rolesGroupQuery'
import {IResRolesGroups} from '../../../types/roles'
import {useGetUser} from '../../../store/userStore'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {REQUEST_PASSWORD_RESET} from '../../../gql/mutations/authMutations'
import {useAuth} from '../../../../modules/auth'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {
  ChannelConfigData,
  TelephonyProviderValue,
  VicidialCampaignOption,
  fetchVicidialCampaignOptions,
  fetchScopedChannelConfig,
  saveScopedChannelConfig,
} from '../../channelConfig/hooks/useChannelConfig'

const INIT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  cellphone: '',
  password: '',
  confirmPassword: '',
  rol: '',
  organizationId: '',
  sendInvitation: true,
  telephony_provider: '' as TelephonyProviderValue,
  vicidial_enabled: false,
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_campaign_id: '',
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  cellphone: Yup.string(),
  rol: Yup.string().required('Role is required'),
  organizationId: Yup.string(),
  sendInvitation: Yup.boolean(),
  password: Yup.string().when('sendInvitation', {
    is: false,
    then: (schema) => schema.min(6, 'Password must be at least 6 characters').required('Password is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  confirmPassword: Yup.string().when('sendInvitation', {
    is: false,
    then: (schema) =>
      schema.oneOf([Yup.ref('password')], 'Passwords must match').required('Confirm password is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  vicidial_agent_user: Yup.string().when(['telephony_provider', 'vicidial_enabled'], {
    is: (telephonyProvider: TelephonyProviderValue, vicidialEnabled: boolean) =>
      telephonyProvider === 'VICIDIAL' || vicidialEnabled,
    then: (schema) => schema.trim().required('PolicyDial agent user is required when PolicyDial is enabled'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

type UserTelephonyPayload = Pick<
  ChannelConfigData,
  | 'telephony_provider'
  | 'vicidial_enabled'
  | 'vicidial_agent_user'
  | 'vicidial_agent_password'
  | 'vicidial_phone_login'
  | 'vicidial_phone_password'
  | 'vicidial_campaign_id'
>

const buildTelephonyPayload = (values: typeof INIT_VALUES): UserTelephonyPayload => ({
  telephony_provider: values.telephony_provider || '',
  vicidial_enabled: Boolean(values.vicidial_enabled),
  vicidial_agent_user: values.vicidial_agent_user.trim(),
  vicidial_agent_password: values.vicidial_agent_password,
  vicidial_phone_login: values.vicidial_phone_login.trim(),
  vicidial_phone_password: values.vicidial_phone_password,
  vicidial_campaign_id: values.vicidial_campaign_id.trim(),
})

const hasTelephonyOverride = (payload: UserTelephonyPayload) =>
  Boolean(
      payload.telephony_provider ||
      payload.vicidial_enabled ||
      payload.vicidial_agent_user ||
      payload.vicidial_agent_password ||
      payload.vicidial_phone_login ||
      payload.vicidial_phone_password ||
      payload.vicidial_campaign_id
  )

interface IModalAddUserProps {
  isOpen: true
  onClose: () => void
  refetch: () => void
  user?: IAllUserNode
}

export const ModalAddUser: React.FC<IModalAddUserProps> = ({isOpen, onClose, refetch, user}) => {
  const {auth} = useAuth()
  // Get current user's role
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id
  const currentUserOrganizationId = currentUser?.organization?.id

  // Check user roles
  const isAgente = userGroups.includes('Agente')
  const isAsesor = userGroups.includes('Asesor')
  const isSupervisor = userGroups.includes('Supervisor')
  const isAdministrador = userGroups.includes('Administrador')
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const isEditingSelf = !!(user && currentUserId && user.id === currentUserId)

  // Determine if role selector should be disabled
  const isRoleSelectorDisabled = !!(isAgente || isAsesor) && isEditingSelf

  const {
    data: dataRoles,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery<IResRolesGroups>(GET_ROLES_GROUP, {fetchPolicy: 'no-cache'})

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {fetchPolicy: 'no-cache'})
  
  const [gqlCreate, {data, loading, error}] = useMutation<IResCreateMember>(CREATE_USER)
  const [gqlEdit, {data: dataEdit, loading: loadingEdit, error: errorEdit}] =
    useMutation<IResEditMember>(EDIT_USER)
  const [requestInvite] = useMutation(REQUEST_PASSWORD_RESET)

  const formik = useFormik({
    initialValues: INIT_VALUES,
    validationSchema,

    onSubmit: (v) => {
      const {
        sendInvitation,
        telephony_provider,
        vicidial_enabled,
        vicidial_agent_user,
        vicidial_agent_password,
        vicidial_phone_login,
        vicidial_phone_password,
        vicidial_campaign_id,
        ...values
      } = v
      const applyDeductionsValue = user ? user.applyDeductions : true
      const telephonyPayload = buildTelephonyPayload(v)
      pendingTelephonyRef.current = {
        shouldPersist: hasTelephonyOverride(telephonyPayload) || Boolean(existingTelephonyConfigIdRef.current),
        payload: telephonyPayload,
      }

      // Si es Administrador o Supervisor, asignar su propia organización automáticamente
      let organizationIdToSend = values.organizationId ? parseId(values.organizationId) : undefined
      if ((isAdministrador || isSupervisor) && !user && currentUserOrganizationId) {
        organizationIdToSend = parseId(currentUserOrganizationId)
      }

      const userData = {
        ...values,
        applyDeductions: applyDeductionsValue,
        confirmPassword: undefined,
        primaryGroup: values.rol ? parseId(values.rol) : undefined,
        organizationId: organizationIdToSend,
        isActive: true,
        rol: undefined,
        password: sendInvitation ? undefined : values.password,
      }
      if (user) {
        gqlEdit({
          variables: {
            userData,
            userId: parseId(user.id),
          },
        })
      } else {
        inviteRequestRef.current = {
          email: values.email,
          enabled: sendInvitation,
        }
        gqlCreate({
          variables: {
            userData,
          },
        })
      }
    },
  })

  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const inviteRequestRef = useRef<{email: string; enabled: boolean} | null>(null)
  const pendingTelephonyRef = useRef<{
    shouldPersist: boolean
    payload: UserTelephonyPayload
  } | null>(null)
  const existingTelephonyConfigIdRef = useRef<number | null>(null)
  const [loadingTelephonyConfig, setLoadingTelephonyConfig] = useState(false)
  const [organizationTelephonyConfig, setOrganizationTelephonyConfig] =
    useState<Partial<ChannelConfigData> | null>(null)
  const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)

  const getApiToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  const effectiveOrganizationId =
    formik.values.organizationId ||
    (((isAdministrador || isSupervisor) && !user && currentUserOrganizationId)
      ? currentUserOrganizationId
      : '')

  const persistPendingTelephonyConfig = async (targetUserId: string) => {
    const pending = pendingTelephonyRef.current
    pendingTelephonyRef.current = null

    if (!pending?.shouldPersist) {
      return true
    }

    const token = getApiToken()
    if (!token) {
      toast.error('User saved, but the PolicyDial mapping could not be saved.')
      return false
    }

    try {
      const savedConfig = await saveScopedChannelConfig({
        scope: 'user',
        targetId: targetUserId,
        token,
        data: pending.payload,
      })
      existingTelephonyConfigIdRef.current = savedConfig.id
      return true
    } catch {
      toast.error('User saved, but the PolicyDial mapping could not be saved.')
      return false
    }
  }

  useEffect(() => {
    if (!isOpen) return

    if (!user) {
      existingTelephonyConfigIdRef.current = null
      pendingTelephonyRef.current = null
      formik.resetForm({values: INIT_VALUES})
      return
    }

    formik.setValues({
      cellphone: user.cellphone || '',
      confirmPassword: '',
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: '',
      rol: user.primaryGroup ? user.primaryGroup.id : '',
      organizationId: user.organization ? user.organization.id : '',
      sendInvitation: true,
      telephony_provider: '',
      vicidial_enabled: false,
      vicidial_agent_user: '',
      vicidial_agent_password: '',
      vicidial_phone_login: '',
      vicidial_phone_password: '',
      vicidial_campaign_id: '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOpen])

  useEffect(() => {
    let cancelled = false

    const loadUserTelephonyConfig = async () => {
      if (!isOpen || !user?.id) return

      const token = getApiToken()
      if (!token) return

      setLoadingTelephonyConfig(true)
      try {
        const config = await fetchScopedChannelConfig({
          scope: 'user',
          targetId: `${parseId(user.id)}`,
          token,
        })

        if (cancelled) return

        existingTelephonyConfigIdRef.current = config.id
        formik.setFieldValue('telephony_provider', config.telephony_provider || '', false)
        formik.setFieldValue('vicidial_enabled', Boolean(config.vicidial_enabled), false)
        formik.setFieldValue('vicidial_agent_user', config.vicidial_agent_user || '', false)
        formik.setFieldValue('vicidial_agent_password', config.vicidial_agent_password || '', false)
        formik.setFieldValue('vicidial_phone_login', config.vicidial_phone_login || '', false)
        formik.setFieldValue('vicidial_phone_password', config.vicidial_phone_password || '', false)
        formik.setFieldValue('vicidial_campaign_id', config.vicidial_campaign_id || '', false)
      } catch {
        if (!cancelled) {
          existingTelephonyConfigIdRef.current = null
          toast.error('Unable to load the PolicyDial mapping for this user.')
        }
      } finally {
        if (!cancelled) {
          setLoadingTelephonyConfig(false)
        }
      }
    }

    loadUserTelephonyConfig()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isOpen, auth?.api_token])

  useEffect(() => {
    let cancelled = false

    const loadOrganizationTelephony = async () => {
      if (!isOpen || !effectiveOrganizationId) {
        setOrganizationTelephonyConfig(null)
        setCampaignOptions([])
        return
      }

      const token = getApiToken()
      if (!token) return

      try {
        const config = await fetchScopedChannelConfig({
          scope: 'organization',
          targetId: `${parseId(effectiveOrganizationId)}`,
          token,
        })

        if (cancelled) return

        setOrganizationTelephonyConfig(config)

        if (
          !config.vicidial_base_url ||
          !config.vicidial_api_user ||
          !config.vicidial_api_password
        ) {
          setCampaignOptions([])
          return
        }

        setLoadingCampaigns(true)
        try {
          const campaigns = await fetchVicidialCampaignOptions({
            token,
            baseUrl: config.vicidial_base_url,
            apiUser: config.vicidial_api_user,
            apiPassword: config.vicidial_api_password,
          })

          if (!cancelled) {
            setCampaignOptions(campaigns)
          }
        } catch {
          if (!cancelled) {
            setCampaignOptions([])
          }
        } finally {
          if (!cancelled) {
            setLoadingCampaigns(false)
          }
        }
      } catch {
        if (!cancelled) {
          setOrganizationTelephonyConfig(null)
          setCampaignOptions([])
        }
      }
    }

    loadOrganizationTelephony()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrganizationId, isOpen, auth?.api_token])

  useEffect(() => {
    if (error) {
      pendingTelephonyRef.current = null
      toast.error(`Error: ${error.message}`)
    }
  }, [error])
  useEffect(() => {
    if (errorEdit) {
      pendingTelephonyRef.current = null
      toast.error(`Error: ${errorEdit.message}`)
    }
  }, [errorEdit])
  useEffect(() => {
    if (errorRoles) toast.error(`Error: ${errorRoles.message}`)
  }, [errorRoles])
  useEffect(() => {
    if (errorOrganizations) toast.error(`Error: ${errorOrganizations.message}`)
  }, [errorOrganizations])

  useEffect(() => {
    if (!data) return
    if (data?.createUserMutation?.user?.id) {
      const inviteRequest = inviteRequestRef.current
      const runInvite = async () => {
        await persistPendingTelephonyConfig(`${parseId(data.createUserMutation.user.id)}`)
        toast.success('user added successfully')
        if (inviteRequest?.enabled) {
          try {
            const response = await requestInvite({
              variables: {email: inviteRequest.email, isInvitation: true},
            })
            const message =
              response.data?.requestPasswordReset?.message || 'Invitation sent.'
            toast.success(message)
          } catch {
            toast.error('Unable to send invitation email.')
          }
        }
        inviteRequestRef.current = null
        refetch()
        onClose()
      }
      runInvite()
    } else if (data?.createUserMutation.errors?.length > 0) {
      toast.error(`Error: ${data?.createUserMutation.errors[0]?.message || 'Something wrong!'}`)
      inviteRequestRef.current = null
      pendingTelephonyRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  useEffect(() => {
    if (!dataEdit) return
    if (dataEdit?.updateUserMutation?.user?.id) {
      const runUpdate = async () => {
        await persistPendingTelephonyConfig(`${parseId(dataEdit.updateUserMutation.user.id)}`)
        toast.success('user updated successfully')
        refetch()
        onClose()
      }
      runUpdate()
    } else if (dataEdit?.updateUserMutation.errors?.length > 0) {
      toast.error(`Error: ${dataEdit?.updateUserMutation.errors[0]?.message || 'Something wrong!'}`)
      pendingTelephonyRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEdit])

  return (
    <Modal show={isOpen} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Invite a new user</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Row className='g-2'>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  First name <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Control
                  placeholder='First name'
                  name='firstName'
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.firstName && formik.touched.firstName)}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Last name <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Control
                  placeholder='Last name'
                  name='lastName'
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.lastName && formik.touched.lastName)}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Email address <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Control
                  placeholder='Email address'
                  type='email'
                  name='email'
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.email && formik.touched.email)}
                />
                <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Cellphone number</Form.Label>
                <Form.Control
                  placeholder='Cellphone'
                  type='tel'
                  name='cellphone'
                  value={formik.values.cellphone}
                  onChange={(e) => {
                    let v = e.target.value
                    // Permite "+" solo al inicio y dígitos; elimina otros caracteres
                    v = v.replace(/(?!^\+)[^\d]/g, '')        // quita todo lo que no sea dígito excepto un '+' inicial
                    v = v.replace(/(?!^)\+/g, '')             // quita '+' extra si los hay
                    formik.setFieldValue('cellphone', v)
                  }}
                  maxLength={15}
                  isInvalid={Boolean(formik.errors.cellphone && formik.touched.cellphone)}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.cellphone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <InputGroup className='mb-3'>
                  <Form.Control
                    type={showPass ? 'text' : 'password'}
                    placeholder='Insert password'
                    aria-label='Insert password'
                    aria-describedby='password'
                    name='password'
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    disabled={formik.values.sendInvitation}
                    isInvalid={Boolean(formik.errors.password && formik.touched.password)}
                  />
                  <Button
                    variant='outline-secondary'
                    id='button-addon2'
                    style={{border: '1px solid #dbdfe9'}}
                    onClick={() => setShowPass((p) => !p)}
                  >
                    <i
                      className={`bi bi-${showPass ? 'eye-slash' : 'eye'} fs-2`}
                      style={{color: 'inherit'}}
                    />
                  </Button>
                </InputGroup>
                {formik.errors.password && formik.touched.password && (
                  <small className='text-danger'>{formik.errors.password}</small>
                )}
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Confirm Password</Form.Label>
                <InputGroup className='mb-3'>
                  <Form.Control
                    type={showConfirmPass ? 'text' : 'password'}
                    placeholder='Confirm password'
                    aria-label='Confirm password'
                    aria-describedby='Confirm password'
                    name='confirmPassword'
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    disabled={formik.values.sendInvitation}
                    isInvalid={Boolean(
                      formik.errors.confirmPassword && formik.touched.confirmPassword
                    )}
                  />
                  <Button
                    variant='outline-secondary'
                    id='Confirm-pass'
                    onClick={() => setShowConfirmPass((p) => !p)}
                    style={{border: '1px solid #dbdfe9'}}
                  >
                    <i
                      className={`bi bi-${showConfirmPass ? 'eye-slash' : 'eye'} fs-2`}
                      style={{color: 'inherit'}}
                    />
                  </Button>
                </InputGroup>
                {formik.errors.confirmPassword && formik.touched.confirmPassword && (
                  <small className='text-danger'>{formik.errors.confirmPassword}</small>
                )}
              </Form.Group>
            </Col>

            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Rol <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Select
                  name='rol'
                  value={formik.values.rol}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(formik.errors.rol && formik.touched.rol)}
                  disabled={loadingRoles || isRoleSelectorDisabled}
                >
                  {loading ? (
                    <option value=''>Loading...</option>
                  ) : (
                    <option value=''>Select One</option>
                  )}
                  {dataRoles?.allGroups.edges
                    .filter(({node}) => {
                      // Si no es SuperAdministrador, no puede asignar rol SuperAdministrador
                      if (!isSuperAdministrador && node.name === 'SuperAdministrador') {
                        return false
                      }
                      // Si es Supervisor, no puede crear o asignar rol de Administrador
                      if (isSupervisor && node.name === 'Administrador') {
                        return false
                      }
                      return true
                    })
                    .map(({node}) => (
                      <option key={node.id} value={node.id}>
                        {node.name}
                      </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type='invalid'>{formik.errors.rol}</Form.Control.Feedback>
                {isRoleSelectorDisabled && (
                  <Form.Text className='text-muted'>
                    You don't have permissions to change your own role
                  </Form.Text>
                )}
              </Form.Group>
              {/* <small>
                <span className='text-primary' style={{textDecoration: 'underline'}}>
                  Click here
                </span>{' '}
                to learn more about our access roles.
              </small> */}
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Organization</Form.Label>
                <Form.Select
                  name='organizationId'
                  value={formik.values.organizationId}
                  onChange={formik.handleChange}
                  isInvalid={Boolean(
                    formik.errors.organizationId && formik.touched.organizationId
                  )}
                  disabled={
                    loadingOrganizations
                    // TEMPORARY: Commented out SuperAdministrador restriction
                    // ((isAdministrador || isSupervisor) && !user) ||
                    // (user && !isSuperAdministrador)
                  }
                >
                  {loadingOrganizations ? (
                    <option value=''>Loading...</option>
                  ) : (
                    <option value=''>Select One</option>
                  )}
                  {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.organizationId}
                </Form.Control.Feedback>
                {((isAdministrador || isSupervisor) && !user) && (
                  <Form.Text className='text-muted'>
                    New users will automatically be assigned to your organization
                  </Form.Text>
                )}
                {user && !isSuperAdministrador && (
                  <Form.Text className='text-muted'>
                    Only SuperAdministrador can change the organization
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} className='mt-4'>
              <div className='border border-dashed border-primary rounded p-4 bg-light-primary'>
                <div className='d-flex justify-content-between align-items-start gap-3 mb-3'>
                  <div>
                    <h6 className='fw-bold mb-1'>Telephony / PolicyDial</h6>
                    <div className='text-muted fs-7'>
                      Optional per-user override. Use it when this PolicyTools user must map to a
                      specific PolicyDial agent or extension.
                    </div>
                  </div>
                  {loadingTelephonyConfig && user && (
                    <div className='text-primary d-flex align-items-center gap-2 fs-7'>
                      <Spinner animation='border' size='sm' />
                      Loading mapping...
                    </div>
                  )}
                  {loadingCampaigns && (
                    <div className='text-primary d-flex align-items-center gap-2 fs-7'>
                      <Spinner animation='border' size='sm' />
                      Loading campaigns...
                    </div>
                  )}
                </div>
                <Row className='g-2'>
                  <Col md={4} sm={12}>
                    <Form.Group>
                      <Form.Label>Telephony provider</Form.Label>
                      <Form.Select
                        name='telephony_provider'
                        value={formik.values.telephony_provider}
                        onChange={formik.handleChange}
                        disabled={loadingTelephonyConfig}
                      >
                        <option value=''>Inherit organization</option>
                        <option value='RINGCENTRAL'>RingCentral</option>
                        <option value='VICIDIAL'>PolicyDial</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12} className='d-flex align-items-end'>
                    <Form.Group>
                      <Form.Check
                        type='switch'
                        name='vicidial_enabled'
                        checked={formik.values.vicidial_enabled}
                        onChange={(e) =>
                          formik.setFieldValue('vicidial_enabled', e.target.checked)
                        }
                        label='Enable PolicyDial override'
                        disabled={loadingTelephonyConfig}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12}>
                    <div className='alert alert-light mb-0 py-3'>
                      <div className='fw-semibold fs-7 mb-1'>Reference</div>
                      <div className='text-muted fs-7'>
                        If the PolicyDial identifier should match PolicyTools, use the same user value
                        here after the account is created.
                      </div>
                    </div>
                  </Col>
                  <Col md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>PolicyDial agent user</Form.Label>
                      <Form.Control
                        placeholder='support01'
                        name='vicidial_agent_user'
                        value={formik.values.vicidial_agent_user}
                        onChange={formik.handleChange}
                        isInvalid={Boolean(
                          formik.errors.vicidial_agent_user && formik.touched.vicidial_agent_user
                        )}
                        disabled={loadingTelephonyConfig}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.vicidial_agent_user}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Agent password</Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        placeholder='Enter PolicyDial agent password'
                        name='vicidial_agent_password'
                        value={formik.values.vicidial_agent_password}
                        onChange={formik.handleChange}
                        disabled={loadingTelephonyConfig}
                      />
                      <Form.Text className='text-muted'>
                        Optional for manual login. Required if you want PolicyTools to auto-login the agent.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Extension / phone login</Form.Label>
                      <Form.Control
                        placeholder='860005'
                        name='vicidial_phone_login'
                        value={formik.values.vicidial_phone_login}
                        onChange={formik.handleChange}
                        disabled={loadingTelephonyConfig}
                      />
                      <Form.Text className='text-muted'>
                        Optional. Store the PolicyDial extension or <code>phone_login</code> assigned
                        to this user.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Phone password</Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        placeholder='Enter PolicyDial phone password'
                        name='vicidial_phone_password'
                        value={formik.values.vicidial_phone_password}
                        onChange={formik.handleChange}
                        disabled={loadingTelephonyConfig}
                      />
                      <Form.Text className='text-muted'>
                        Used together with the extension for PolicyDial auto-login.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Campaign</Form.Label>
                      {campaignOptions.length > 0 ? (
                        <Form.Select
                          name='vicidial_campaign_id'
                          value={formik.values.vicidial_campaign_id}
                          onChange={formik.handleChange}
                          disabled={loadingTelephonyConfig}
                        >
                          <option value=''>
                            Inherit organization
                            {organizationTelephonyConfig?.vicidial_campaign_id
                              ? ` (${organizationTelephonyConfig.vicidial_campaign_id})`
                              : ' (no default campaign)'}
                          </option>
                          {campaignOptions.map((campaign) => (
                            <option key={campaign.campaign_id} value={campaign.campaign_id}>
                              {campaign.campaign_id} - {campaign.campaign_name}
                              {campaign.active === 'Y' ? '' : ' (Inactive)'}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          placeholder={
                            organizationTelephonyConfig?.vicidial_campaign_id ||
                            'Inherit organization campaign'
                          }
                          name='vicidial_campaign_id'
                          value={formik.values.vicidial_campaign_id}
                          onChange={formik.handleChange}
                          disabled={loadingTelephonyConfig}
                        />
                      )}
                      <Form.Text className='text-muted'>
                        Leave this blank to inherit the organization campaign
                        {organizationTelephonyConfig?.vicidial_campaign_id
                          ? ` (${organizationTelephonyConfig.vicidial_campaign_id})`
                          : ''}.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>
            {/* <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Preferred language <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Select>
                  <option>Spanish</option>
                  <option>English</option>
                </Form.Select>
              </Form.Group>
            </Col> */}
            {!user && (
              <Col className='mt-5'>
                <Form.Check
                  type='checkbox'
                  name='sendInvitation'
                  checked={formik.values.sendInvitation}
                  onChange={(e) => {
                    const checked = e.target.checked
                    formik.setFieldValue('sendInvitation', checked)
                    if (checked) {
                      formik.setFieldValue('password', '')
                      formik.setFieldValue('confirmPassword', '')
                    }
                  }}
                  label={
                    <span className='text-dark'>
                      Send invitation email to set credentials.
                    </span>
                  }
                />
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Close
          </Button>
          <Button type='submit' variant='primary' disabled={loading || loadingEdit}>
            {(loading || loadingEdit) && <Spinner animation='border' size='sm' />}
            Save Changes
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
